<template>
  <div 
    v-if="!loading" 
    class="content">
    <div class="container">
      <div class="row">
        <div class="col-12">
          <n-button
            type="info"
            class="mt-0 px-0"
            link
            @click.native="back">
            <i class="bx bx-chevron-left"/> View all offers
          </n-button>
        </div>
        <div class="col-xl-8 col-lg-7">
          <div class="row align-items-center">
            <div class="col-auto">
              <img 
                :src="offer.locationLogo"
                class="img img-raised rounded logo">
            </div>
            <div class="col">
              <div class="row align-items-center">
                <div class="col-lg-12 col-auto">
                  <h3 class="mb-2">{{ offer.locationName }}</h3>
                </div>
                <div class="col-auto">
                  <h6 class="category text-primary">{{ offer.name }}</h6>
                </div>
                <div 
                  v-if="offer.reviewSummary" 
                  class="col-auto">
                  <star-rating 
                    v-model="offer.reviewSummary.averageStars"
                    :increment="0.1" 
                    :show-rating="false"
                    :star-size="20"
                    :inline="true"
                    :read-only="true"/>
                </div>
                <div 
                  v-if="offer.reviewSummary"
                  class="col pl-0">
                  ({{ offer.reviewSummary ? offer.reviewSummary.total : 0 }} Reviews)
                </div>
              </div>
              <div class="row d-none d-md-flex">
                <div 
                  v-for="amenitie in offer.amenities"
                  :key="amenitie.name" 
                  class="col-lg-auto col-md-6">
                  <div
                    class="amenitie">
                    <i 
                      :class="amenitie.icon" 
                      class="bx mr-1"/>{{ amenitie.name }}
                  </div>
                </div>
              </div>
              <div 
                v-if="offer.coordinate" 
                class="row">
                <div class="col-12">
                  <a 
                    :href="`http://maps.google.com/maps?q=${offer.coordinate.coordinates[1]},${offer.coordinate.coordinates[0]}`" 
                    target="_blank"
                    class="text-info">{{ offer.address }}</a>
                </div>
              </div>
            </div>
          </div>
          <div class="row d-flex d-md-none py-2">
            <div 
              v-for="amenitie in offer.amenities"
              :key="amenitie.name" 
              class="col-sm-auto">
              <div
                class="amenitie">
                <i 
                  :class="amenitie.icon" 
                  class="bx mr-1"/>{{ amenitie.name }}
              </div>
            </div>
          </div>
          <div class="row my-3">
            <div class="col-md-12">
              <VueSlickCarousel
                ref="image"
                :arrows="false"
                :as-nav-for="$refs.thumbs"
                :focus-on-select="true">
                <div 
                  v-for="(image, index) in offer.images"
                  :key="index"
                  :style="{ backgroundImage: 'url(' + image + ')' }"
                  class="carousel-item" />
              </VueSlickCarousel>
              <VueSlickCarousel
                ref="thumbs"
                :arrows="false"
                :as-nav-for="$refs.image"
                :slides-to-show="5"
                :focus-on-select="true">
                <div
                  v-for="(image, index) in offer.images"
                  :key="index">
                  <div 
                    :style="{ backgroundImage: 'url(' + image + ')' }"
                    class="carousel-thumb-item" />
                </div>
              </VueSlickCarousel>
            </div>
          </div>
          
          <div class="d-block d-md-none form-reserve">
            <h4 class="mt-0">Check In / Check Out</h4>
            <APCDateRangePicker
              :initial-range="initialRange"
              :max-length="options.maxReservationDays" 
              @update="updateRange" />
            <div>
              <div class="row align-items-center mb-1">
                <div class="col-6">
                  Length: 
                </div>
                <div class="col-6 pl-0">
                  <h5 class="font-weight-normal m-0">{{ length }}</h5>
                </div>
              </div>
              <div class="row align-items-center mb-1">
                <div class="col-6">
                  Daily Rate: 
                </div>
                <div class="col-6 pl-0">
                  <h5 class="font-weight-normal m-0">{{ offer.dailyBaseRate | currency }}</h5>
                </div>
              </div>
              <div class="row align-items-center mb-1">
                <div class="col-6">
                  Total Daily: 
                </div>
                <div class="col-6 pl-0">
                  <h5 class="font-weight-normal m-0">{{ offer.cost.totalBaseRate | currency }}</h5>
                </div>
              </div>
              <div class="row align-items-center mb-1">
                <div class="col-6">
                  Processing Fee<i 
                    class="bx bx-help-circle bx-xs help-icon" 
                    @click="openTip('Processing Fee', processingFeeContent)"/>:
                </div>
                <div class="col-6 pl-0">
                  <h5 class="font-weight-normal m-0">{{ offer.cost.appliedProcessingFee.amount | currency }}</h5>
                </div>
              </div>
              <div class="row align-items-center mb-1">
                <div class="col-12">
                  <el-collapse accordion>
                    <el-collapse-item name="1">
                      <template slot="title">
                        <div class="col-6">
                          <i class="bx bx-plus mr-1" />
                          <i class="bx bx-minus mr-1" />
                          
                          Location Fees: 
                        </div>
                        <div class="col-6 pl-0">
                          <h5 class="font-weight-normal m-0">{{ offer.cost.totalFees | currency }}</h5>
                        </div>
                      </template>
                      <ul>
                        <li 
                          v-for="fee in offer.cost.fees"
                          :key="fee.id">
                          <div class="row align-items-center">
                            <div class="col-6">
                              {{ fee.description }}:
                            </div>
                            <div class="col-6 pl-0">
                              <h5 class="font-weight-normal m-0">{{ fee.amount | currency }}</h5>
                            </div>
                          </div>
                        </li>
                      </ul>
                    </el-collapse-item>
                  </el-collapse>
                </div>
              </div>
              <div class="row align-items-center mb-1">
                <div class="col-6">
                  Total: 
                </div>
                <div class="col-6 pl-0">
                  <h5 class="font-weight-bold m-0">{{ offer.cost.totalRate | currency }}</h5>
                </div>
              </div>
            </div>
            <h4 class="mb-0">Customer Info</h4>
            <div 
              :class="accessToken ? 'mt-3' : ''" 
              class="row">
              <div class="col-12 text-right">
                <n-button
                  v-if="!accessToken" 
                  type="info" 
                  block
                  @click.native="setLoginModal(true)">
                  Login for Faster Checkout
                  <i class="bx bxs-chevron-right" />
                </n-button>              
              </div>
            </div>
            <ValidationObserver 
              v-slot="{ handleSubmit }" 
              ref="reserveForm">
              <form 
                class="form"
                @submit.prevent="handleSubmit(onReserve)">
                <ValidationProvider 
                  name="Email" 
                  rules="required|email">
                  <div slot-scope="{ errors }">
                    <label>Email</label>
                    <fg-input
                      :value="customer.email"
                      :readonly="accessToken ? true : false"
                      :error="errors[0]"
                      addon-left-icon="now-ui-icons ui-1_email-85"
                      placeholder="Email"
                      @input="setCustomerInfo('email', $event.target.value)"/>
                  </div>
                </ValidationProvider>
                <div class="row">
                  <div class="col-md-6">
                    <ValidationProvider
                      name="First Name" 
                      rules="required">
                      <div slot-scope="{ errors }">
                        <label>First Name</label>
                        <fg-input
                          :value="customer.firstname"
                          :readonly="accessToken ? true : false"
                          :error="errors[0]"
                          placeholder="First Name"
                          addon-left-icon="now-ui-icons users_circle-08"
                          @input="setCustomerInfo('firstname', $event.target.value)"/>
                      </div>
                    </ValidationProvider>
                  </div>
                  <div class="col-md-6">
                    <ValidationProvider
                      name="Last Name" 
                      rules="required">
                      <div slot-scope="{ errors }">
                        <label>Last Name</label>
                        <fg-input
                          :value="customer.lastname"
                          :readonly="accessToken ? true : false"
                          :error="errors[0]"
                          placeholder="Last Name"
                          addon-left-icon="now-ui-icons users_circle-08"
                          @input="setCustomerInfo('lastname', $event.target.value)"/>
                      </div>
                    </ValidationProvider>
                  </div>
                </div>
                <ValidationProvider 
                  :rules="{required: true, regex: phoneRegex}"
                  name="Phone">
                  <div slot-scope="{ errors }">
                    <label>Phone</label>
                    <fg-input
                      :value="customer.phone"
                      :readonly="accessToken ? true : false"
                      :error="errors[0]"
                      addon-left-icon="now-ui-icons tech_mobile"
                      placeholder="Phone"
                      @input="setCustomerInfo('phone', $event.target.value)"/>
                  </div>
                </ValidationProvider>
                <ValidationProvider 
                  :rules="{required: offer.requireLicensePlate}"
                  name="License Plate">
                  <div slot-scope="{ errors }">
                    <label>License Plate
                      <i 
                        class="bx bx-help-circle bx-xs help-icon" 
                        @click="openTip('License Plate', licensePlate)"/>
                    </label>
                    <fg-input 
                      :value="customer.licensePlate"
                      :error="errors[0]"
                      addon-left-icon="now-ui-icons business_briefcase-24"
                      placeholder="License Plate"
                      @input="setCustomerInfo('licensePlate', $event.target.value)"/>
                  </div>
                </ValidationProvider>
                <ValidationProvider
                  v-if="!accessToken" 
                  :rules="{required: accessToken ? false : true, min: accessToken ? 0 : 6}"
                  name="Password">
                  <div slot-scope="{ errors }">
                    <label>Password</label>
                    <fg-input 
                      :value="customer.password"
                      :error="errors[0]"
                      type="password"
                      addon-left-icon="now-ui-icons ui-1_lock-circle-open"
                      placeholder="Password"
                      @input="setCustomerInfo('password', $event.target.value)"/>
                  </div>
                </ValidationProvider>
                <h4>Payment Options
                  <i 
                    class="bx bx-help-circle bx-sm help-icon" 
                    @click="openTip('Payment Options', paymentOptions)"/>
                </h4>
                <card v-if="validCard">
                  <div class="row align-items-center">
                    <div class="col-auto pr-0">
                      <img 
                        :src="'img/card-icons/' + customer.cardsOnFile.cardType.toLowerCase() + '.svg'"
                        class="img cc-icon">
                    </div>
                    <div class="col-auto cc-number">**** **** **** {{ customer.cardsOnFile.lastFour.substr(4) }}
                    </div>
                    <div class="col cc-expire">
                      Expire: {{ customer.cardsOnFile.expirationDate }}
                    </div>
                  </div>
                </card>
                <card @click.native="openCardModal=true">
                  <div class="row align-items-center">
                    <div class="col-auto pr-0">
                      <img 
                        src="img/card-icons/default.svg"
                        class="img cc-icon">
                    </div>
                    <div class="col-auto cc-number">{{ validCard ? 'Change' : 'Add' }} Card
                    </div>
                  </div>
                </card>
                <n-button 
                  :disabled="saving" 
                  native-type="submit"
                  type="danger" 
                  block>
                  {{ saving ? '' : 'Reserve' }}
                  <i 
                    v-if="saving" 
                    class="bx bx-loader bx-spin"/>
                </n-button>
              </form>
            </ValidationObserver>
          </div>

          <!-- Details Start -->
          <div class="row">
            <div class="col-12">
              <h4 class="mt-0">Details</h4>
              <collapse
                :active-index="0">
                <collapse-item 
                  v-for="(section, index) in offer.sections"
                  :key="index"
                  :title="section.name">
                  <template v-slot:title>
                    {{ section.name }}
                  </template>
                  <template v-slot:icon>
                    <i class="bx bx-plus mr-1" />
                    <i class="bx bx-minus mr-1" />
                  </template>
                  <div v-html="section.text" />
                </collapse-item>
              </collapse>
            </div>
          </div>
          <!-- Details End -->

          <!-- Reviews Start -->
          <div 
            v-if="offer.reviewSummary" 
            class="row d-md-block d-none">
            <div class="col-12">
              <h4>Reviews</h4>
              <VueSlickCarousel
                :arrows="reviewArrows"
                :as-nav-for="$refs.thumbs"
                :focus-on-select="true"
                :slides-to-show="reviewShows">
                <card 
                  v-for="review in offer.reviewSummary.reviews"
                  :key="review.id"
                  type="testimonial" 
                  plain
                  @click.native="openReview(review)">
                  <h5 class="card-title">{{ review.reviewerName }}</h5>
                  <star-rating
                    v-model="review.stars"
                    :increment="0.1" 
                    :show-rating="false"
                    :star-size="16"
                    :inline="true"
                    :read-only="true"/>
                  <p class="card-description">"{{ review.reviewText }}"</p>
                  <div class="review-date">{{ dateFormat(review.reviewDate) }}</div>
                </card>
              </VueSlickCarousel>
            </div>
          </div>
          <!-- Reviews End -->
        </div>
        <div class="col-xl-4 col-lg-5">
          <div class="d-none d-md-block">
            <h4 class="mt-0">Check In / Check Out</h4>
            <APCDateRangePicker
              :initial-range="initialRange"
              :max-length="options.maxReservationDays" 
              @update="updateRange" />
            <div>
              <div class="row align-items-center mb-1">
                <div class="col-6">
                  Length: 
                </div>
                <div class="col-6 pl-0">
                  <h5 class="font-weight-normal m-0">{{ length }}</h5>
                </div>
              </div>
              <div class="row align-items-center mb-1">
                <div class="col-6">
                  Daily Rate: 
                </div>
                <div class="col-6 pl-0">
                  <h5 class="font-weight-normal m-0">{{ offer.dailyBaseRate | currency }}</h5>
                </div>
              </div>
              <div class="row align-items-center mb-1">
                <div class="col-6">
                  Total Daily: 
                </div>
                <div class="col-6 pl-0">
                  <h5 class="font-weight-normal m-0">{{ offer.cost.totalBaseRate | currency }}</h5>
                </div>
              </div>
              <div class="row align-items-center mb-1">
                <div class="col-6">
                  Processing Fee<i 
                    class="bx bx-help-circle bx-xs help-icon" 
                    @click="openTip('Processing Fee', processingFeeContent)"/>:
                </div>
                <div class="col-6 pl-0">
                  <h5 class="font-weight-normal m-0">{{ offer.cost.appliedProcessingFee.amount | currency }}</h5>
                </div>
              </div>
              <div class="row align-items-center mb-1">
                <div class="col-12">
                  <el-collapse accordion>
                    <el-collapse-item name="1">
                      <template slot="title">
                        <div class="col-6">
                          <i class="bx bx-plus mr-1" />
                          <i class="bx bx-minus mr-1" />
                          
                          Location Fees: 
                        </div>
                        <div class="col-6 pl-0">
                          <h5 class="font-weight-normal m-0">{{ offer.cost.totalFees | currency }}</h5>
                        </div>
                      </template>
                      <ul>
                        <li 
                          v-for="fee in offer.cost.fees"
                          :key="fee.id">
                          <div class="row align-items-center">
                            <div class="col-6">
                              {{ fee.description }}:
                            </div>
                            <div class="col-6 pl-0">
                              <h5 class="font-weight-normal m-0">{{ fee.amount | currency }}</h5>
                            </div>
                          </div>
                        </li>
                      </ul>
                    </el-collapse-item>
                  </el-collapse>
                </div>
              </div>
              <div class="row align-items-center mb-1">
                <div class="col-6">
                  Total: 
                </div>
                <div class="col-6 pl-0">
                  <h5 class="font-weight-bold m-0">{{ offer.cost.totalRate | currency }}</h5>
                </div>
              </div>
            </div>
            <h4 class="mb-0">Customer Info</h4>
            <div 
              :class="accessToken ? 'mt-3' : ''" 
              class="row">
              <div class="col-12 text-right">
                <n-button
                  v-if="!accessToken" 
                  type="info" 
                  block
                  @click.native="setLoginModal(true)">
                  Login for Faster Checkout
                  <i class="bx bxs-chevron-right" />
                </n-button>              
              </div>
            </div>
            <ValidationObserver 
              v-slot="{ handleSubmit }" 
              ref="reserveForm">
              <form 
                class="form"
                @submit.prevent="handleSubmit(onReserve)">
                <ValidationProvider 
                  name="Email" 
                  rules="required|email">
                  <div slot-scope="{ errors }">
                    <label>Email</label>
                    <fg-input
                      :value="customer.email"
                      :readonly="accessToken ? true : false"
                      :error="errors[0]"
                      addon-left-icon="now-ui-icons ui-1_email-85"
                      placeholder="Email"
                      @input="setCustomerInfo('email', $event.target.value)"/>
                  </div>
                </ValidationProvider>
                <div class="row">
                  <div class="col-md-6">
                    <ValidationProvider
                      name="First Name" 
                      rules="required">
                      <div slot-scope="{ errors }">
                        <label>First Name</label>
                        <fg-input
                          :value="customer.firstname"
                          :readonly="accessToken ? true : false"
                          :error="errors[0]"
                          placeholder="First Name"
                          addon-left-icon="now-ui-icons users_circle-08"
                          @input="setCustomerInfo('firstname', $event.target.value)"/>
                      </div>
                    </ValidationProvider>
                  </div>
                  <div class="col-md-6">
                    <ValidationProvider
                      name="Last Name" 
                      rules="required">
                      <div slot-scope="{ errors }">
                        <label>Last Name</label>
                        <fg-input
                          :value="customer.lastname"
                          :readonly="accessToken ? true : false"
                          :error="errors[0]"
                          placeholder="Last Name"
                          addon-left-icon="now-ui-icons users_circle-08"
                          @input="setCustomerInfo('lastname', $event.target.value)"/>
                      </div>
                    </ValidationProvider>
                  </div>
                </div>
                <ValidationProvider 
                  :rules="{required: true, regex: phoneRegex}"
                  name="Phone">
                  <div slot-scope="{ errors }">
                    <label>Phone</label>
                    <fg-input
                      :value="customer.phone"
                      :readonly="accessToken ? true : false"
                      :error="errors[0]"
                      addon-left-icon="now-ui-icons tech_mobile"
                      placeholder="Phone"
                      @input="setCustomerInfo('phone', $event.target.value)"/>
                  </div>
                </ValidationProvider>
                <ValidationProvider 
                  :rules="{required: offer.requireLicensePlate}"
                  name="License Plate">
                  <div slot-scope="{ errors }">
                    <label>License Plate
                      <i 
                        class="bx bx-help-circle bx-xs help-icon" 
                        @click="openTip('License Plate', licensePlate)"/>
                    </label>
                    <fg-input 
                      :value="customer.licensePlate"
                      :error="errors[0]"
                      addon-left-icon="now-ui-icons business_briefcase-24"
                      placeholder="License Plate"
                      @input="setCustomerInfo('licensePlate', $event.target.value)"/>
                  </div>
                </ValidationProvider>
                <ValidationProvider
                  v-if="!accessToken" 
                  :rules="{required: accessToken ? false : true, min: accessToken ? 0 : 6}"
                  name="Password">
                  <div slot-scope="{ errors }">
                    <label>Password</label>
                    <fg-input 
                      :value="customer.password"
                      :error="errors[0]"
                      type="password"
                      addon-left-icon="now-ui-icons ui-1_lock-circle-open"
                      placeholder="Password"
                      @input="setCustomerInfo('password', $event.target.value)"/>
                  </div>
                </ValidationProvider>
                <h4>Payment Options
                  <i 
                    class="bx bx-help-circle bx-sm help-icon" 
                    @click="openTip('Payment Options', paymentOptions)"/>
                </h4>
                <card v-if="validCard">
                  <div class="row align-items-center">
                    <div class="col-auto pr-0">
                      <img 
                        :src="'img/card-icons/' + customer.cardsOnFile.cardType.toLowerCase() + '.svg'"
                        class="img cc-icon">
                    </div>
                    <div class="col-auto cc-number">**** **** **** {{ customer.cardsOnFile.lastFour.substr(4) }}
                    </div>
                    <div class="col cc-expire">
                      Expire: {{ customer.cardsOnFile.expirationDate }}
                    </div>
                  </div>
                </card>
                <card @click.native="openCardModal=true">
                  <div class="row align-items-center">
                    <div class="col-auto pr-0">
                      <img 
                        src="img/card-icons/default.svg"
                        class="img cc-icon">
                    </div>
                    <div class="col-auto cc-number">{{ validCard ? 'Change' : 'Add' }} Card
                    </div>
                  </div>
                </card>
                <n-button 
                  :disabled="saving" 
                  native-type="submit"
                  type="danger" 
                  block>
                  {{ saving ? '' : 'Reserve' }}
                  <i 
                    v-if="saving" 
                    class="bx bx-loader bx-spin"/>
                </n-button>
              </form>
            </ValidationObserver>
          </div>
          <div 
            v-if="offer.reviewSummary" 
            class="row d-block d-md-none">
            <div class="col-12">
              <h4>Reviews</h4>
              <VueSlickCarousel
                :arrows="reviewArrows"
                :as-nav-for="$refs.thumbs"
                :focus-on-select="true"
                :slides-to-show="reviewShows">
                <card 
                  v-for="review in offer.reviewSummary.reviews"
                  :key="review.id"
                  type="testimonial" 
                  plain
                  @click.native="openReview(review)">
                  <h5 class="card-title">{{ review.reviewerName }}</h5>
                  <star-rating
                    v-model="review.stars"
                    :increment="0.1" 
                    :show-rating="false"
                    :star-size="16"
                    :inline="true"
                    :read-only="true"/>
                  <p class="card-description">"{{ review.reviewText }}"</p>
                  <div class="review-date">{{ dateFormat(review.reviewDate) }}</div>
                </card>
              </VueSlickCarousel>
            </div>
          </div>
        </div>
      </div>
      <n-button
        v-if="scrollButton"
        type="primary"
        class="btn-scroll"
        @click.native="scrollToReserve">
        Go to Reserve
      </n-button>
    </div>

    <ReviewModal
      :open="openReviewModal"
      :data="review"
      @close="openReviewModal=false" />

    <TipModal
      :open="openTipModal"
      :title="tipTitle"
      :content="tipContent"
      @close="openTipModal=false" />

    <CardModal 
      :open="openCardModal"
      @close="openCardModal=false"
      @save="setCard" />

    <ReceiptModal
      :open="openReceiptModal"
      :data="receipt"
      @close="onCloseReceipt" />
  </div>
</template>

<script>
  import StarRating from 'vue-star-rating';
  import VueSlickCarousel from 'vue-slick-carousel';
  import 'vue-slick-carousel/dist/vue-slick-carousel.css';
  import 'vue-slick-carousel/dist/vue-slick-carousel-theme.css';
  import moment from 'moment';
  import axios from 'axios';
  import { mapState, mapMutations } from 'vuex';
  import { Collapse as ElCollapse, CollapseItem as ElCollapseItem, Notification } from 'element-ui';
  import { Button, Card, Collapse, CollapseItem, DropDown, FormGroupInput } from '@/components';
  import APCDateRangePicker from '@/pages/components/APCDateRangePicker.vue';
  import ReviewModal from '@/pages/components/ReviewModal.vue';
  import TipModal from '@/pages/components/TipModal.vue';
  import CardModal from '@/pages/components/CardModal.vue';
  import ReceiptModal from '@/pages/components/ReceiptModal.vue';
  import { PHONE_REGEX } from '@/constants';

  const PROCESSING_FEE = `
    <ul>
      <li>1-9 Days Booked - $5 service fee</li>
      <li>10-19 Days Booked - $10 service fee</li>
      <li>20-29 Days Booked - $15 service fee</li>
      <li>30-39 Days Booked - $20 service fee</li>
      <li>more then 40 Days Booked - $25 service fee</li>
    </ul>
    <p><b>Note:</b> This is just a ONE TIME service fee charge at the time of checkout. No additional reservation fees will be charged to the customer.</p>
  `;
  const LICENSE_PLATE = `
    License plates are required in some facilities who use LPR (License plate recognition) systems to make your entry and exit faster. It is simply used as a credential for efficiency
  `;
  const PAYMENT_OPTIONS = `
    You need to add a card for payment purposes, when adding you can chose to save the card and we will add the card to your profile for quick checkout of future purposes. But you can also just use it as a one time payment and we will not store.<br /><br />
    Also note that we do not actually store store your card info we use carefully selected payment processing gateway partners who store, manage, and tokenize your card in the securest way possible to eliminate any possible CC fraud/theft issues in strictest accordance with PCI standards.
  `;

  export default {
    components: {
      StarRating,
      VueSlickCarousel,
      [ElCollapse.name]: ElCollapse,
      [ElCollapseItem.name]: ElCollapseItem,
      [Button.name]: Button,
      Card,
      Collapse,
      CollapseItem,
      [DropDown.name]: DropDown,
      [FormGroupInput.name]: FormGroupInput,
      APCDateRangePicker,
      ReviewModal,
      TipModal,
      CardModal,
      ReceiptModal
    },
    props: {
      initialOffer: {
        type: Object,
        default: null
      }
    },
    data() {
      return {
        processingFeeContent: PROCESSING_FEE,
        licensePlate: LICENSE_PLATE,
        paymentOptions: PAYMENT_OPTIONS,
        id: null,
        name: null,
        code: null,
        lat: null,
        lng: null,
        offer: {},
        reviewShows: 3,
        reviewArrows: true,
        openReviewModal: false,
        review: {},
        length: '',
        customer: {},
        openTipModal: false,
        tipTitle: '',
        tipContent: '',
        phoneRegex: PHONE_REGEX,
        cardErr: false,
        openCardModal: false,
        token: null,
        tokenDescription: null,
        saveCardsOnFile: false,
        saving: false,
        openReceiptModal: false,
        receipt: null,
        scrollButton: false,
      }
    },
    computed: {
      ...mapState([
        'accessToken',
        'member',
        'loading',
        'options'
      ]),
      initialRange() {
        return {       
          startDate: moment(this.offer.startDate + ' ' + this.offer.startTime).toDate(),
          endDate: moment(this.offer.endDate + ' ' + this.offer.endTime).toDate()
        }
      },
      validCard() {
        if (this.customer.cardsOnFile) {
          const { cardType, expirationDate, lastFour } = this.customer.cardsOnFile;
          return cardType && expirationDate && lastFour;
        } else {
          return false;
        }
      }
    },
    watch: {
      offer() {
        this.getLength();
      },
      member() {
        this.customer = this.member;
        if (this.$refs.reserveForm) {
          this.$refs.reserveForm.reset();
        }
      }
    },
    created() {
      if (this.accessToken) {
        this.validateToken();
      }
      if (this.$route.query.id) {
        this.id = this.$route.query.id;
      }
      if (this.$route.query.start) {
        this.offer.startDate = this.$route.query.start.split(' ')[0];
        this.offer.startTime = this.$route.query.start.split(' ')[1];
      }
      if (this.$route.query.end) {
        this.offer.endDate = this.$route.query.end.split(' ')[0];
        this.offer.endTime = this.$route.query.end.split(' ')[1];
      }
      if (this.$route.query.code) {
        this.code = this.$route.query.code;
      }
      if (this.$route.query.name) {
        this.name = this.$route.query.name;
      }
      if (this.$route.query.lat) {
        this.lat = this.$route.query.lat;
      }
      if (this.$route.query.lng) {
        this.lng = this.$route.query.lng;
      }
      if (!this.initialOffer) {
        this.getOffer();
      } else {
        this.offer = this.initialOffer;
        this.setLoading(false);
      }

      if (window.innerWidth < 576) {
        this.reviewShows = 1;
      } else if (window.innerWidth < 1200) {
        this.reviewShows = 2;
      } else {
        this.reviewShows = 3;
      }

      if (window.innerWidth < 992) {
        this.scrollButton = true;
      }  else {
        this.scrollButton = false;
      }
      window.addEventListener('resize', () => {
        if (window.innerWidth < 768) {
          this.reviewShows = 1;
        } else if (window.innerWidth < 1200) {
          this.reviewShows = 2;
        } else {
          this.reviewShows = 3;
        }

        if (window.innerWidth < 992) {
          this.scrollButton = true;
        }  else {
          this.scrollButton = false;
        }
      })
    },
    methods: {
      ...mapMutations([
        'setLoading',
        'setLoginModal',
        'setAccessToken',
        'setMember'
      ]),
      back() {
        const params = { 
          name: this.name,
          code: this.code,
          start: this.offer.startDate + ' ' + this.offer.startTime, 
          end: this.offer.endDate + ' ' + this.offer.endTime,
          lat: this.lat,
          lng: this.lng
        };
        this.$router.push({ name: '/offers', query: { ...params } });
      },
      getOffer() {
        const { startDate, startTime, endDate, endTime } = this.offer;
        axios.get(`offer/update?offerTemplateId=${this.id}&startDate=${startDate}&startTime=${startTime}&endDate=${endDate}&endTime=${endTime}`).then((res) => {
          const { data } = res;
          console.log('offer: ', data);
          this.offer = data;
          this.setLoading(false);
        })
      },
      validateToken() {
        axios.get('member/validate').then(res => {
          this.customer = this.member;
        }).catch(err => {
          console.error('member/validate: ', err.response);
          localStorage.clear();
          this.setAccessToken(null);
          this.setMember({});
        })
      },
      dateFormat(date) {
        return moment(date).format('MM/DD/YYYY')
      },
      openReview(review) {
        setTimeout(() => {
          this.review = review;
          this.openReviewModal = true;
        }, 0);
      },
      getLength(date = null) {
        let diff;
        if (!date) {
          diff = moment(moment(this.offer.endDate + ' ' + this.offer.endTime).toDate()).diff(moment(moment(this.offer.startDate + ' ' + this.offer.startTime).toDate()), 'minutes');
        } else {
          diff = moment(date.endDate).diff(moment(date.startDate), 'minutes');
        }
        const days = Math.floor(diff / 60 / 24);
        const hours = Math.floor((diff - days * 60 * 24) / 60);
        const mins = diff - days * 24 * 60 - hours * 60;
        
        let length = days + 'Days ';
        if (hours > 0) {
          length += hours + 'Hrs '
        }
        if (mins > 0) {
          length += mins + 'Mins';
        }

        this.length = length.trim();
      },
      updateRange(date) {
        const start = moment(date.startDate).format('YYYY-MM-DD HH:mm');
        const end = moment(date.endDate).format('YYYY-MM-DD HH:mm');
        if (start !== this.offer.startDate + ' ' + this.offer.startTime || end !== this.offer.endDate + ' ' + this.offer.endTime) {
          console.log('update offer called');
          this.getLength(date);

          this.offer.startDate = start.split(' ')[0];
          this.offer.startTime = start.split(' ')[1];
          this.offer.endDate = end.split(' ')[0];
          this.offer.endTime = end.split(' ')[1];
          this.$router.push({ name: '/reservation', query: {
            id: this.offer.offerTemplateId,
            start,
            end,
            name: this.name,
            code: this.code,
            lat: this.lat,
            lng: this.lng
          }});

          this.getOffer();
        }
      },
      setCustomerInfo(key, value) {
        this.customer = {
          ...this.customer,
          [key]: value
        }
      },
      onReserve() {
        if (!this.validCard) {
          this.showNotification('error', 'Card Error', 'Please add a card');
          return;
        }

        this.saving = true;
        if (this.accessToken) {
          this.callReserve();
        } else {
          this.registerMember();
        }
      },
      callReserve() {
        const body = {
          offerTemplateId: this.offer.offerTemplateId,
          token: this.token,
          tokenDescription: this.tokenDescription,
          licensePlate: this.customer.licensePlate,
          startDate: this.offer.startDate,
          startTime: this.offer.startTime,
          endDate: this.offer.endDate,
          endTime: this.offer.endTime
        }
        if (this.saveCardsOnFile) {
          delete body['token'];
          delete body['tokenDescription'];
        }
        axios.post('reservation', body).then(res => {
          this.saveCardsOnFile = false;
          this.saving = false;
          this.openReceiptModal = true;
          this.receipt = res.data;
          
          this.$router.push({name: '/confirmation', params: {data: this.receipt}});
        }).catch(err => {
          const { data } = err.response;
          console.error('reservation: ', data);
          this.showNotification('error', 'Failed', data.responseStatus.message);
          this.saving = false;
        })
      },
      onCloseReceipt() {
        this.openReceiptModal = false;
        this.receipt = null;
      },
      setCard({token, tokenDescription, cardsOnFile, save}) {
        this.token = token;
        this.tokenDescription = tokenDescription;
        this.saveCardsOnFile = save;
        this.customer = {
          ...this.customer,
          cardsOnFile
        }
        if (this.accessToken && save) {
          this.saveCard();
        }
      },
      saveCard(autoReserve = false) {
        axios.post('member/cardonfile', {
          noonceToken: this.token,
          noonceDescriptor: this.tokenDescription
        }).then(res => {
          const { data } = res;
          console.log('member/cardonfile: ', data);
          const member = {
            ...this.member,
            cardsOnFile: data.cardsOnFile
          };
          localStorage.setItem('member', JSON.stringify(member));
          this.setMember(member)
          if (autoReserve) {
            this.callReserve();
          }
        })
      },
      registerMember() {
        const body = Object.assign({}, this.customer);
        delete body['cardsOnFile'];
        axios.post('member', body).then(res => {
          const { data } = res;
          this.setMemberData(data);
          if (this.saveCardsOnFile) {
            this.saveCard(true);
          } else {
            this.callReserve();
          }
        }).catch(err => {
          console.error('member: ', err.response);

          axios.post('member/login', {
            email: this.customer.email,
            password: this.customer.password
          }).then(res => {
            const { data } = res;
            this.setMemberData(data);
            if (!this.validCard) {
              this.showNotification('error', 'Card Error', 'Please add a card');
              this.saving = false;
            } else {
              this.callReserve();
            }
          }).catch(err => {
            console.error('member/login: ', err.response);
            this.showNotification('error', 'Login Error', 'Email exist. Please login with correct password');
            this.saving = false;
          })
        })
      },
      setMemberData(data) {
        localStorage.setItem('accessToken', data.accessToken);
        this.setAccessToken(data.accessToken);
        localStorage.setItem('member', JSON.stringify(data.member));
        this.setMember(data.member);
      },
      showNotification(type, title, message) {
        let customClass = '';
        if (type === 'error') {
          customClass = 'alert-danger';
        } else if (type === 'success') {
          customClass = 'alert-success';
        }
        Notification({
          type: type,
          title: title,
          message: message,
          customClass: customClass,
          offset: 80,
          showClose: false,
        });
      },
      scrollToReserve() {
        const el = this.$el.getElementsByClassName("form-reserve")[0];
        el.scrollIntoView();
      },
      openTip(title, content) {
        this.tipTitle = title;
        this.tipContent = content;
        this.openTipModal = true;
      }
    }
  }
</script>

<style lang="scss" scoped>
  .logo {
    height: 120px;

    @media (max-width: 992px) {
      height: 100px
    }
  }
  .carousel-item {
    height: 350px;
    background-image: url('https://cdn.AirportParking.Club/Location/Image/2020/10/23/5f92fd0651302bbe814e10f0.png'); 
    background-repeat: no-repeat;
    background-size: cover;
    background-position: center;

    @media (max-width: 992px) {
      height: 300px; 
    }
  }
  .carousel-thumb-item {
    height: 100px;
    background-image: url('https://cdn.AirportParking.Club/Location/Image/2020/10/23/5f92fd0651302bbe814e10f0.png'); 
    background-repeat: no-repeat;
    background-size: cover;
    background-position: center;
    margin-left: 4px;
    margin-right: 4px;

    @media (max-width: 992px) {
      height: 80px; 
    }
  }
  ::v-deep .card-collapse {
    margin-left: 16px;

    .card {
      margin-bottom: 8px;

      .card-header {
        a {
          .bx {
            float: left;
          }
          .bx-plus {
            display: inline-block;
          }
          .bx-minus {
            display: none;
          }
        }
        a[aria-expanded="true"] {
          .bx {
            float: left;
          }
          .bx-plus {
            display: none;
          }
          .bx-minus {
            display: inline-block;
          }
        }
      }
    }
  }
  p {
    color: #2c2c2c;
    font-size: 1.1em;
  }

  ::v-deep .slick-prev {
    left: 0;
  }
  ::v-deep .slick-next {
    right: 0;
  }
  ::v-deep .slick-prev, ::v-deep .slick-next {
    z-index: 99;
    &::before {
      color: #555357;
    }
  }
  .card-testimonial {
    margin-top: 0;

    .card-description {
      margin-bottom: 4px;
      white-space: inherit;
      overflow: hidden;
      text-overflow: ellipsis;
      display: -webkit-box;
      -webkit-line-clamp: 3;
      -webkit-box-orient: vertical;
      // cursor: pointer;
    }
    .review-date {
      text-align: right;
      font-size: 0.8em;
      font-weight: 400;
    }
  }

  .help-icon {
    position: relative;
    top: 2px;
    margin-left: 2px;
    cursor: pointer;
  }

  ::v-deep .el-collapse {
    border: 0 !important;

    .el-collapse-item__header {
      height: auto !important;
      line-height: unset !important;
      font-size: inherit !important;
      margin-bottom: 4px;
      border: 0 !important;
      margin-left: -15px;
      margin-right: -15px;

      .bx-plus {
        display: inline-block;
      }
      .bx-minus {
        display: none;
      }

      &.is-active {
        .bx-plus {
          display: none;
        }
        .bx-minus {
          display: inline-block;
        }
      }
    }

    .el-collapse-item__wrap {
      border: 0 !important;

      .el-collapse-item__content {
        padding-bottom: 4px;
      }
    }

    .el-collapse-item__arrow {
      display: none;
    }
  }

  .cc-icon {
    height: 20px;
  }
  .cc-number {
    font-size: 1.1em;
    font-weight: bold;
  }
  .cc-expire {
    font-size: 0.9em;
    font-weight: normal;
    text-align: right;
  }

  .btn-scroll {
    position: fixed;
    bottom: 0;
    right: 16px;
  }
</style>